import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  doc,
  runTransaction,
  updateDoc,
  where,
  getDocs,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { signInWithPopup, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { db, auth, googleProvider } from '../config/firebase';
import styles from './CompassFeedback.module.css';

interface FeedbackItem {
  id: string;
  title: string;
  description: string;
  authorUid: string;
  authorName: string;
  authorPhotoURL: string | null;
  upvotes: number;
  status: 'open' | 'planned' | 'in-progress' | 'done';
  createdAt: Timestamp;
}

const STATUS_CONFIG: Partial<Record<FeedbackItem['status'], { label: string; className: string }>> = {
  planned: { label: 'Planned', className: styles.statusPlanned },
  'in-progress': { label: 'In Progress', className: styles.statusInProgress },
  done: { label: 'Done', className: styles.statusDone },
};

const LogoIcon = () => (
  <img src="/images/compass/logo.png" alt="" aria-hidden="true" className={styles.logoIcon} />
);

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={styles.googleIcon}>
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

function formatDate(ts: Timestamp): string {
  const date = ts.toDate();
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export const CompassFeedback: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [userVotes, setUserVotes] = useState<Set<string>>(new Set());
  const [votingId, setVotingId] = useState<string | null>(null);

  const isAdmin = currentUser?.email === 'dixonappdev.dad@gmail.com';

  const [sortBy, setSortBy] = useState<'top' | 'new'>('top');
  const [filterStatus, setFilterStatus] = useState<'all' | FeedbackItem['status']>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<'submit' | 'vote' | null>(null);
  const [submitTitle, setSubmitTitle] = useState('');
  const [submitDescription, setSubmitDescription] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setAuthLoading(false);
      if (user) {
        await loadUserVotes(user.uid);
      } else {
        setUserVotes(new Set());
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'trackhabits-feedback'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      const items: FeedbackItem[] = snapshot.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<FeedbackItem, 'id'>),
      }));
      setFeedbackItems(items);
      setFeedbackLoading(false);
    });
    return unsub;
  }, []);

  const loadUserVotes = async (uid: string) => {
    const q = query(collection(db, 'trackhabits-votes'), where('uid', '==', uid));
    const snapshot = await getDocs(q);
    const voted = new Set<string>();
    snapshot.forEach((d) => voted.add(d.data().feedbackId as string));
    setUserVotes(voted);
  };

  const handleSignIn = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch {
      // user cancelled
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
  };

  const handleVote = async (feedbackId: string) => {
    if (!currentUser) {
      setPendingAction('vote');
      setShowAuthModal(true);
      return;
    }

    setVotingId(feedbackId);
    const voteDocId = `${currentUser.uid}_${feedbackId}`;
    const voteRef = doc(db, 'trackhabits-votes', voteDocId);
    const feedbackRef = doc(db, 'trackhabits-feedback', feedbackId);

    try {
      const hasVoted = userVotes.has(feedbackId);
      await runTransaction(db, async (tx) => {
        const [voteDoc, fbDoc] = await Promise.all([tx.get(voteRef), tx.get(feedbackRef)]);
        if (!fbDoc.exists()) throw new Error('Feedback not found');
        const current = (fbDoc.data().upvotes as number) || 0;
        if (voteDoc.exists()) {
          tx.delete(voteRef);
          tx.update(feedbackRef, { upvotes: Math.max(0, current - 1) });
        } else {
          tx.set(voteRef, { uid: currentUser.uid, feedbackId, votedAt: serverTimestamp() });
          tx.update(feedbackRef, { upvotes: current + 1 });
        }
      });

      setUserVotes((prev) => {
        const next = new Set(prev);
        if (hasVoted) next.delete(feedbackId);
        else next.add(feedbackId);
        return next;
      });
    } catch {
      // silently ignore
    } finally {
      setVotingId(null);
    }
  };

  const handleStatusChange = async (feedbackId: string, status: FeedbackItem['status']) => {
    await updateDoc(doc(db, 'trackhabits-feedback', feedbackId), { status });
  };

  const handleAuthModalSignIn = async () => {
    setShowAuthModal(false);
    await handleSignIn();
    if (pendingAction === 'submit') {
      setShowSubmitModal(true);
    }
    setPendingAction(null);
  };

  const handleSubmitClick = () => {
    if (!currentUser) {
      setPendingAction('submit');
      setShowAuthModal(true);
      return;
    }
    setShowSubmitModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    if (!submitTitle.trim()) {
      setSubmitError('Title is required.');
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      await addDoc(collection(db, 'trackhabits-feedback'), {
        title: submitTitle.trim().slice(0, 100),
        description: submitDescription.trim().slice(0, 500),
        authorUid: currentUser.uid,
        authorName: currentUser.displayName || 'Anonymous',
        authorPhotoURL: currentUser.photoURL || null,
        upvotes: 0,
        status: 'open',
        createdAt: serverTimestamp(),
      });
      setSubmitTitle('');
      setSubmitDescription('');
      setShowSubmitModal(false);
    } catch {
      setSubmitError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const displayItems = useMemo(() => {
    let items = [...feedbackItems];

    if (filterStatus !== 'all') {
      items = items.filter((i) => i.status === filterStatus);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (i) => i.title.toLowerCase().includes(q) || i.description.toLowerCase().includes(q)
      );
    }

    if (sortBy === 'top') {
      items.sort((a, b) => b.upvotes - a.upvotes);
    } else {
      items.sort((a, b) => {
        const aTime = a.createdAt?.seconds ?? 0;
        const bTime = b.createdAt?.seconds ?? 0;
        return bTime - aTime;
      });
    }

    return items;
  }, [feedbackItems, filterStatus, searchQuery, sortBy]);

  return (
    <div className={styles.page}>
      <a href="#main-content" className={styles.skipLink}>Skip to main content</a>

      <nav className={`${styles.nav} ${isScrolled ? styles.navScrolled : ''}`}>
        <div className={styles.navContainer}>
          <Link to="/compass" className={styles.logo}>
            <LogoIcon />
            <span>Compass</span>
          </Link>

          <div className={styles.navRight}>
            {!authLoading && (
              currentUser ? (
                <div className={styles.userInfo}>
                  {currentUser.photoURL && (
                    <img src={currentUser.photoURL} alt="" className={styles.avatar} referrerPolicy="no-referrer" />
                  )}
                  <span className={styles.userName}>{currentUser.displayName?.split(' ')[0]}</span>
                  <button onClick={handleSignOut} className={styles.signOutBtn}>Sign out</button>
                </div>
              ) : (
                <button onClick={handleSignIn} className={styles.signInBtn}>
                  <GoogleIcon />
                  Sign in
                </button>
              )
            )}
          </div>
        </div>
      </nav>

      <main id="main-content" className={styles.main}>
        <div className={styles.header}>
          <h1 className={styles.title}>Feature Requests</h1>
          <p className={styles.subtitle}>
            Vote on the features you want most. The top-voted requests get built first.
          </p>
        </div>

        <div className={styles.controls}>
          <div className={styles.searchWrap}>
            <svg viewBox="0 0 20 20" fill="currentColor" className={styles.searchIcon}>
              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
            </svg>
            <input
              type="text"
              placeholder="Search requests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          <div className={styles.filters}>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'top' | 'new')}
              className={styles.select}
            >
              <option value="top">Top Voted</option>
              <option value="new">Newest</option>
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
              className={styles.select}
            >
              <option value="all">All</option>
              <option value="planned">Planned</option>
              <option value="in-progress">In Progress</option>
              <option value="done">Done</option>
            </select>
          </div>

          <button onClick={handleSubmitClick} className={styles.submitBtn}>
            <svg viewBox="0 0 20 20" fill="currentColor" className={styles.plusIcon}>
              <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
            </svg>
            Submit a Request
          </button>
        </div>

        <div className={styles.list}>
          {feedbackLoading ? (
            <div className={styles.empty}>
              <div className={styles.spinner} />
            </div>
          ) : displayItems.length === 0 ? (
            <div className={styles.empty}>
              <p className={styles.emptyText}>
                {searchQuery || filterStatus !== 'all'
                  ? 'No requests match your filters.'
                  : 'No feature requests yet. Be the first to submit one!'}
              </p>
            </div>
          ) : (
            displayItems.map((item) => {
              const hasVoted = userVotes.has(item.id);
              const isVoting = votingId === item.id;
              const statusConfig = STATUS_CONFIG[item.status];

              return (
                <div key={item.id} className={styles.card}>
                  <button
                    onClick={() => handleVote(item.id)}
                    disabled={isVoting}
                    className={`${styles.voteBtn} ${hasVoted ? styles.voteBtnActive : ''}`}
                    aria-label={hasVoted ? 'Remove upvote' : 'Upvote'}
                    aria-pressed={hasVoted}
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" className={styles.voteArrow}>
                      <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
                    </svg>
                    <span className={styles.voteCount}>{item.upvotes}</span>
                  </button>

                  <div className={styles.cardBody}>
                    <div className={styles.cardTop}>
                      <h2 className={styles.cardTitle}>{item.title}</h2>
                      {isAdmin ? (
                        <select
                          value={item.status}
                          onChange={(e) => handleStatusChange(item.id, e.target.value as FeedbackItem['status'])}
                          className={`${styles.adminStatusSelect} ${styles[`adminStatus_${item.status.replace('-', '_')}`]}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="open">Open</option>
                          <option value="planned">Planned</option>
                          <option value="in-progress">In Progress</option>
                          <option value="done">Done</option>
                        </select>
                      ) : statusConfig && (
                        <span className={`${styles.statusBadge} ${statusConfig.className}`}>{statusConfig.label}</span>
                      )}
                    </div>
                    {item.description && (
                      <p className={styles.cardDescription}>{item.description}</p>
                    )}
                    <div className={styles.cardMeta}>
                      {item.authorPhotoURL && (
                        <img src={item.authorPhotoURL} alt="" className={styles.metaAvatar} referrerPolicy="no-referrer" />
                      )}
                      <span className={styles.metaAuthor}>{item.authorName}</span>
                      <span className={styles.metaDot}>·</span>
                      <span className={styles.metaDate}>
                        {item.createdAt ? formatDate(item.createdAt) : '—'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>

      {showSubmitModal && (
        <div className={styles.modalOverlay} onClick={() => setShowSubmitModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Submit a Feature Request</h2>
              <button onClick={() => setShowSubmitModal(false)} className={styles.modalClose} aria-label="Close">
                <svg viewBox="0 0 20 20" fill="currentColor" width={20} height={20}>
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="fb-title" className={styles.label}>
                  Title <span className={styles.required}>*</span>
                </label>
                <input
                  id="fb-title"
                  type="text"
                  value={submitTitle}
                  onChange={(e) => setSubmitTitle(e.target.value)}
                  placeholder="e.g. Dark mode support"
                  maxLength={100}
                  className={styles.input}
                  autoFocus
                />
                <span className={styles.charCount}>{submitTitle.length}/100</span>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="fb-desc" className={styles.label}>Details</label>
                <textarea
                  id="fb-desc"
                  value={submitDescription}
                  onChange={(e) => setSubmitDescription(e.target.value)}
                  placeholder="Describe the feature and why it would be useful..."
                  maxLength={500}
                  rows={4}
                  className={styles.textarea}
                />
                <span className={styles.charCount}>{submitDescription.length}/500</span>
              </div>

              {submitError && <p className={styles.formError}>{submitError}</p>}

              <div className={styles.formActions}>
                <button type="button" onClick={() => setShowSubmitModal(false)} className={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" disabled={submitting || !submitTitle.trim()} className={styles.submitFormBtn}>
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAuthModal && (
        <div className={styles.modalOverlay} onClick={() => setShowAuthModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Sign in to continue</h2>
              <button onClick={() => setShowAuthModal(false)} className={styles.modalClose} aria-label="Close">
                <svg viewBox="0 0 20 20" fill="currentColor" width={20} height={20}>
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>
            <p className={styles.authModalText}>
              {pendingAction === 'submit'
                ? 'You need to sign in to submit a feature request.'
                : 'You need to sign in to vote on feature requests.'}
            </p>
            <button onClick={handleAuthModalSignIn} className={styles.googleSignInBtn}>
              <GoogleIcon />
              Sign in with Google
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
